import { useState } from "react";
import { useNavigate } from "react-router";
import { Id } from "react-toastify";

import {
  BaseLogo,
  getLogoType,
  getProductPrice,
  getDesignProductPrice,
} from "../../../../core/models/entities/BaseLogo";
import { useCartContext } from "../../../../contexts/CartContext";
import { trackBeginCheckout } from "../../../../utils/functions/analytics";
import {
  CartItem,
  CartItemDesign,
  CartItemDesignInstance,
  CartItemDesignInstanceLogo,
  CartItemLogo,
  QuantityRow,
} from "../models/CartItem";
import { Design, DesignLogo } from "../../../../core/models/entities/Design";
import {
  DeliveryType,
  Order,
  OrderDeliveryPreferences,
  OrderLine,
  OrderLineDesign,
  OrderLineLogo,
  OrderStatus,
  Packaging,
} from "../../../../core/models/entities/Order";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { CasingOption } from "../../../shared/functions/casing";
import {
  ExtraLogo,
  HeatingOption,
} from "../../order-confirmation/dialogs/add-heating-fee-order-dialog";
import {
  dissmissToast,
  useErrorToast,
  useLoadingToast,
  withToasts,
} from "../../../components/Toast/Toast";
import {
  DesignTemplate,
  LogoTemplate,
} from "../../../../core/models/entities/DesignTemplate";
import { OrderDto } from "../../../../data/models/responses/OrderDto";
import { getDesignLogoPlacementMap } from "../functions/getDesignLogoPlacement";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Price } from "../../../../core/models/entities/Price";
import { Organization } from "../../../../core/models/entities/Organization";
import { Profile } from "../../../../core/models/entities/Profile";

import { useApiClient } from "../../../../data/clients/ApiClient";

// Name and number logos share the same SKU across all typed values, so the
// size suffix adds noise without adding identity. Static logos (TextLogo,
// ImageLogo, SymbolLogo) keep the size — it distinguishes variants that may
// otherwise look the same in the list.
function formatLogoTitleWithSize(
  title: string,
  baseLogo: BaseLogo | undefined,
): string {
  if (!baseLogo) return title;
  const skipSize =
    baseLogo.logoType === "NameLogo" || baseLogo.logoType === "NumberLogo";
  return skipSize
    ? title
    : `${title} ${baseLogo.widthMm}x${baseLogo.heightMm}mm`;
}

export function useOrderActionHandler() {
  const {
    addLogoItem: addLogoToCart,
    addLogoItems: addLogosToCart,
    addDesignItem: addDesignToCart,
    addDesignItems: addDesignsToCart,
    removeItem: removeFromCart,
    updateItem: updateInCart,
    clearItems,
    items,
    designs,
  } = useCartContext();
  const { orderRepository } = useRepositoriesContext();
  const { addItems, activeOrder, setActiveOrder } = useCartContext();
  const { currency, organization, profile } = useProfileContext();

  const [isLoading, setIsLoading] = useState(false);
  const startLoading = () => setIsLoading(true);
  const stopLoading = () => setIsLoading(false);
  const navigate = useNavigate();

  function updateLogoOrderLine(
    logo: BaseLogo,
    deliveryQuantities?: QuantityRow[],
    storageQuantities?: QuantityRow[],
    casing?: CasingOption,
  ) {
    const exists = items.find((x) => x.id === "" + logo.id) as CartItemLogo;
    const updatedOrderLine = {
      ...exists,
      deliveryQuantities: deliveryQuantities ?? exists?.deliveryQuantities ?? 0,
      storageQuantities: storageQuantities ?? exists?.storageQuantities ?? 0,
      casing: casing ?? exists?.casing,
    } as CartItemLogo;

    updateInCart({ ...updatedOrderLine });
  }

  function handleLogoAddOrderLine(
    baseLogo: BaseLogo,
    toDelivery: number,
    toStorage: number,
  ) {
    const existingLogo = items.find((x) => x.id === baseLogo.id.toString()) as
      | CartItemLogo
      | undefined;

    const newLogo = mapBaseLogoToCartItemLogo(
      baseLogo,
      existingLogo ? existingLogo.toDelivery + toDelivery : toDelivery,
      existingLogo ? existingLogo.toStorage + toStorage : toStorage,
    );

    addLogoToCart(newLogo)
      .then()
      .catch(() => {
        useErrorToast("Can't mix different vendor products");
      });
  }

  function handleLogoAddOrderLines(
    baseLogos: BaseLogo[],
    toDelivery: number,
    toStorage: number,
  ) {
    const newLogos = baseLogos.map((baseLogo) => {
      const existingLogo = items.find(
        (x) => x.id === baseLogo.id.toString(),
      ) as CartItemLogo | undefined;

      const newLogo = mapBaseLogoToCartItemLogo(
        baseLogo,
        existingLogo ? existingLogo.toDelivery + toDelivery : toDelivery,
        existingLogo ? existingLogo.toStorage + toStorage : toStorage,
      );

      return newLogo;
    });

    addLogosToCart(newLogos)
      .then()
      .catch(() => {
        useErrorToast("Can't mix different vendor products");
      });
  }

  function mapBaseLogoToCartItemLogo(
    baseLogo: BaseLogo,
    toDelivery: number,
    toStorage: number,
  ): CartItemLogo {
    const itemLogo: CartItemLogo = {
      id: baseLogo.id.toString(),
      title: baseLogo.title,
      type: "Logo",
      displayImage: baseLogo.displayImage!,
      discriminator: baseLogo.logoType,
      baseLogo: baseLogo,
      toStorage: toStorage,
      toDelivery: toDelivery,
      value: undefined,
      logoId: baseLogo.id,
      logoVariant: getLogoType(baseLogo),
      isNumeric: baseLogo.logoType === "NumberLogo",
      deliveryQuantities:
        toDelivery > 0 ? [{ label: "", quantity: toDelivery }] : [],
      storageQuantities:
        toStorage > 0 ? [{ label: "", quantity: toStorage }] : [],
    };

    return itemLogo;
  }

  function handleDesignAddOrderLine(
    design: Design,
    quantity: number,
    options?: { size?: string; label?: string },
    excludedDeletedDesignLogos?: DesignLogo[],
  ) {
    const itemQuantity = items.find((x) => +x.id === design.id) as
      | CartItemDesign
      | undefined;
    const placeholderLogos = design.designLogos;
    const placementLabels = getDesignLogoPlacementMap(design);

    const instances = [
      ...(itemQuantity?.instances ?? []),
      {
        logos: placeholderLogos.map(
          (x) =>
            ({
              logoVariant: getLogoType(x.logo),
              locationIdentifier:
                placementLabels.get(x.inDesignId) ??
                x.viewName?.toUpperCase?.() ??
                "",
              value: "",
              logoId: x.logo.id,
              baseLogo: x.logo,
              designLogoInDesignId: x.inDesignId,
            }) as CartItemDesignInstanceLogo,
        ),
        label: options?.label ?? "New Label",
        toDelivery: quantity,
        toStorage: 0,
        size: options?.size ?? "M",
      },
    ];

    const cartItemDesign = mapDesignToCartItemLogo(
      design,
      instances,
      excludedDeletedDesignLogos,
    );

    addDesignToCart(cartItemDesign)
      .then()
      .catch(() => {
        useErrorToast("Can't mix different vendor products");
      });
  }

  function mapDesignToCartItemLogo(
    design: Design,
    instances: CartItemDesignInstance[],
    excludedDeletedDesignLogos?: DesignLogo[],
  ): CartItemDesign {
    const newDesign: CartItemDesign = {
      id: design.id.toString(),
      title: design.title,
      type: "Design",
      displayImage: design.displayImage!,
      design: design,
      instances: instances,
      excludedDeletedDesignLogos,
    };
    return newDesign;
  }

  function handleDesignAddOrderLines(
    designs: Design[],
    quantity: number,
    options?: { size?: string; label?: string },
    excludedDeletedDesignLogos?: Map<number, DesignLogo[]>,
  ) {
    const newDesigns = designs.map((design) => {
      const itemQuantity = items.find((x) => +x.id === design.id) as
        | CartItemDesign
        | undefined;
      const placeholderLogos = design.designLogos;
      const placementLabels = getDesignLogoPlacementMap(design);
      const newDesign = {
        id: design.id.toString(),
        quantity: quantity,
        title: design.title,
        type: "Design",
        displayImage: design.displayImage!,
        design: design,
        logosToStorage: 0,
        logosToDelivery: quantity,
        excludedDeletedDesignLogos: excludedDeletedDesignLogos?.get(design.id),
        instances: [
          ...(itemQuantity?.instances ?? []),
          {
            isNumeric: false,
            logos: placeholderLogos.map(
              (x) =>
                ({
                  logoVariant: getLogoType(x.logo),
                  locationIdentifier:
                    placementLabels.get(x.inDesignId) ??
                    x.viewName?.toUpperCase?.() ??
                    "",
                  value: "",
                  logoId: x.logo.id,
                  baseLogo: x.logo,
                  designLogoInDesignId: x.inDesignId,
                }) as CartItemDesignInstanceLogo,
            ),
            label: options?.label ?? "Temp",
            toDelivery: quantity,
            toStorage: 0,
            size: options?.size ?? "M",
          },
        ],
      } as CartItemDesign;

      return newDesign;
    });

    addDesignsToCart(newDesigns)
      .then()
      .catch(() => {
        useErrorToast("Can't mix different vendor products");
      });
  }

  function handleRemoveOrderLine(id: string) {
    removeFromCart(id);
  }

  function handleLogoSetQuantities(
    baseLogo: BaseLogo,
    toDelivery: number,
    toStorage: number,
  ) {
    const exists = items.find((x) => x.id === "" + baseLogo.id);
    if (!exists) {
      handleLogoAddOrderLine(baseLogo, toDelivery, toStorage);
      return;
    }

    const updatedLogo = {
      ...exists,
      toDelivery: toDelivery,
      toStorage: toStorage,
    } as CartItemLogo;

    updateInCart({ ...updatedLogo });
  }

  function handlePlaceVerdict(order: Order, verdict: Verdict) {
    const loadingMessage = verdict.approve
      ? "Placing order. This may take a moment, please don't close the application."
      : "Rejecting order. Please don't close the application.";
    const successMessage = verdict.approve
      ? "Order placed successfully"
      : "Verdict succesful";

    withToasts(
      () => orderRepository.placeVerdict(order, verdict),
      loadingMessage,
      successMessage,
      "Failed to place order",
      setIsLoading,
    );
  }

  function handleDesignInstanceUpdateStorage(orderlineDesign: CartItemDesign) {
    const newOrderLineDesign = { ...orderlineDesign } as CartItemDesign;

    if (newOrderLineDesign.instances === undefined) {
      newOrderLineDesign.instances = [];
    }

    updateInCart({ ...newOrderLineDesign });
  }

  async function handleCreateOrder(
    orgId: number,
    items: CartItem[],
    heatingOption: HeatingOption,
    extraLogos: ExtraLogo[],
  ): Promise<Order> {
    setIsLoading(true);
    const heatingOrderLines = applyHeatingOption(items, heatingOption);
    const extraOrderLines = applyExtraLogos(heatingOrderLines, extraLogos);
    const allLogoOrderLines = applyLogoVariantLines(extraOrderLines);

    // NOTE: This snippet filters name logos with empty inputs. Thankfully, it is not needed at the moment...
    // const filteredOrderLines = allLogoOrderLines.filter((item) =>
    //   item.type !== 'Logo' ||
    //   !['name', 'number'].includes((item as CartItemLogo).logoVariant ?? 'name') ||
    //   !!(item as CartItemLogo).value
    // )

    // Build a local draft Order to render the confirmation page instantly.
    const draftOrder = buildLocalOrderDraft(
      orgId,
      allLogoOrderLines,
      currency ?? "DKK",
      organization,
      profile,
    );
    setActiveOrder(draftOrder);
    setIsLoading(false);
    trackBeginCheckout(allLogoOrderLines.length);
    return Promise.resolve(draftOrder);
  }

  function applyHeatingOption(
    orderlines: CartItem[],
    heatingOption: HeatingOption,
  ): CartItem[] {
    const logos = orderlines.filter((x) => x.type === "Logo") as CartItemLogo[];
    const designs = orderlines
      .filter((x) => x.type === "Design")
      .map((x) => x as CartItemDesign);

    if (heatingOption === HeatingOption.none) {
      // Clone existing logos to avoid mutating the original array
      const newLogos = logos.map((l) => ({ ...l }));
      const preservedDesigns: CartItemDesign[] = [];

      designs.forEach((designItem) => {
        const hasApplicationOnlyLogo = designItem.instances.some((inst) =>
          inst.logos.some((l) => {
            const designLogo = designItem.design.designLogos?.find(
              (d) => d.logoId === l.logoId,
            );
            const baseLogo = designLogo?.logo ?? l.baseLogo;
            const type = baseLogo?.transferMethod?.type?.toLowerCase() ?? "";
            return (
              type === "broderi" ||
              type === "embroidery" ||
              type === "direk tryk" ||
              type === "direkte tryk"
            );
          }),
        );

        if (hasApplicationOnlyLogo) {
          preservedDesigns.push(designItem);
          return;
        }

        designItem.instances.forEach((instance) => {
          const instanceQuantity =
            (instance.toDelivery || 0) + (instance.toStorage || 0);
          if (instanceQuantity <= 0) return;

          instance.logos.forEach((instanceLogo) => {
            const designLogo = designItem.design.designLogos?.find(
              (d) => d.logoId === instanceLogo.logoId,
            );
            // Use fresh backend data if available, otherwise fallback to baseLogo
            const baseLogo = designLogo?.logo ?? instanceLogo.baseLogo;

            if (!baseLogo) return;

            // Find existing logo in our accumulation list
            let existingLogo = newLogos.find((l) => l.logoId === baseLogo.id);

            if (!existingLogo) {
              // Create new logo item
              existingLogo = mapBaseLogoToCartItemLogo(baseLogo, 0, 0);
              // Clear default quantity created by mapBaseLogoToCartItemLogo because we will add specific quantities
              existingLogo.deliveryQuantities = [];
              existingLogo.storageQuantities = [];
              newLogos.push(existingLogo);
            }

            // Update quantities
            // "No Heating" means we deliver the transfers.
            existingLogo.toDelivery += instanceQuantity;

            // Handle variable data (Name/Number)
            if (
              baseLogo.logoType === "NameLogo" ||
              baseLogo.logoType === "NumberLogo"
            ) {
              const value = instanceLogo.value?.toString() || "";
              // Add to deliveryQuantities
              const existingQtyRow = existingLogo.deliveryQuantities.find(
                (q) => q.label === value,
              );
              if (existingQtyRow) {
                existingQtyRow.quantity += instanceQuantity;
              } else {
                existingLogo.deliveryQuantities.push({
                  label: value,
                  quantity: instanceQuantity,
                });
              }
            }
          });
        });
      });

      return [...preservedDesigns, ...newLogos];
    }

    const processedDesigns = designs.map((design) => {
      return {
        ...design,
        instances: design.instances.map((instance) => {
          return {
            ...instance,
            logos: instance.logos.map((logos) => {
              return {
                ...logos,
                deliveryType:
                  heatingOption === HeatingOption.all ||
                  heatingOption === HeatingOption.partly
                    ? "Application"
                    : "Delivery",
              };
            }) as CartItemDesignInstanceLogo[],
          };
        }) as CartItemDesignInstance[],
      };
    });
    return [...processedDesigns, ...logos];
  }

  function applyExtraLogos(orderlines: CartItem[], extraLogos: ExtraLogo[]) {
    const cartItemLogos: CartItemLogo[] = orderlines.filter(
      (l) => l.type === "Logo",
    ) as CartItemLogo[];
    cartItemLogos.forEach((i) => {
      const extra = extraLogos.find((e) => e.logo.id.toString() == i.id);
      if (extra) {
        if (extra.option === "toDelivery") i.toDelivery += extra.quantity || 0;
        else if (extra.option === "toStorage")
          i.toStorage += extra.quantity || 0;
      }
    });
    // NOTE: Apart from remvoing empty quantities,
    //  this mess also removes extra logos that are already in the cart,
    //  because their quantity has already been tallied above.
    const extra = extraLogos
      .filter(
        (extra) =>
          !cartItemLogos.find((l) => l.baseLogo.id === extra.logo.id) &&
          extra.quantity > 0,
      )
      .map((logo) =>
        mapBaseLogoToCartItemLogo(
          logo.logo,
          logo.option === "toDelivery" ? logo.quantity : 0,
          logo.option === "toStorage" ? logo.quantity : 0,
        ),
      );
    return [...orderlines, ...extra];
  }

  function applyLogoVariantLines(orderlines: CartItem[]) {
    const logoOrderLines = orderlines.filter(
      (line) => line.type === "Logo",
    ) as CartItemLogo[];
    const logoLines: CartItemLogo[] = [];
    const nameLogoIds: string[] = [];

    for (const logoLine of logoOrderLines) {
      // Skip NumberLogo processing as it's now handled directly in the mapping logic
      if (
        ["TextLogo", "ImageLogo", "NumberLogo"].includes(logoLine.discriminator)
      )
        continue;

      // Track NameLogo IDs that have variant lines created
      if (logoLine.discriminator === "NameLogo") {
        const deliveries = logoLine.deliveryQuantities ?? [];
        const storages = logoLine.storageQuantities ?? [];

        // Only create variant lines if there are actual quantities with labels
        if (deliveries.length > 0 || storages.length > 0) {
          nameLogoIds.push(logoLine.id);
        }
      }

      const deliveries = logoLine.deliveryQuantities ?? [];
      for (const delivery of deliveries) {
        logoLines.push({
          ...logoLine,
          value: delivery.label,
          toDelivery: delivery.quantity,
          // Ensure we do not create storage lines for logos to delivery.
          toStorage: 0,
        });
      }
      const storages = logoLine.storageQuantities ?? [];
      for (const storage of storages) {
        if (storage.label)
          logoLines.push({
            ...logoLine,
            value: storage.label,
            toStorage: storage.quantity,
            // Ensure we do not create delivery lines for logos to storage.
            toDelivery: 0,
          });
      }
    }

    // Filter out original NameLogo order lines that have variant lines created
    const filteredOrderlines = orderlines.filter((orderline) => {
      if (orderline.type === "Logo") {
        const logoLine = orderline as CartItemLogo;
        return !(
          logoLine.discriminator === "NameLogo" &&
          nameLogoIds.includes(logoLine.id)
        );
      }
      return true;
    });

    return [...filteredOrderlines, ...logoLines];
  }

  function handleDownloadTemplate(
    orgId: number,
    designItems: CartItemDesign[],
  ) {
    startLoading();
    orderRepository
      .downloadTemplate(orgId, designItems)
      .then((blobData) => {
        const url = URL.createObjectURL(blobData);
        const a = document.createElement("a");
        a.href = url;
        a.download = `designs_${designItems
          .map((x) => x.design.product?.mul || x.id)
          .join("_")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch((x) => console.log("Error", x))
      .finally(() => {
        stopLoading();
      });
  }

  function handleUploadDesignTemplate(orgId: number, file: File) {
    async function transaction() {
      return await orderRepository.uploadDesignTemplate(orgId, file);
    }

    startLoading();
    return transaction()
      .then((res) => {
        updateDesignWithTemplate(res);
      })
      .finally(stopLoading);
  }

  function handleUploadLogoTemplate(
    orgId: number,
    file: File,
  ): Promise<LogoTemplate> {
    async function transaction() {
      return await orderRepository.uploadLogoTemplate(orgId, file);
    }

    startLoading();
    return transaction()
      .then((res) => {
        return res;
      })
      .finally(stopLoading);
  }

  function updateDesignWithTemplate(template: DesignTemplate) {
    const cartItemDesign = designs.find(
      (x) => Number(x.id) === template.designs[0].designId,
    )!;
    const designLogos = cartItemDesign.design.designLogos;
    const placementLabels = getDesignLogoPlacementMap(cartItemDesign.design);

    // TODO Test if instance was just missing.
    const instances = template.designs[0].instances.map((instance) => {
      return {
        label: instance.label,
        toDelivery: instance.quantity,
        toStorage: 0,
        size: instance.size,
        logos: instance.logos.map((logo) => {
          const designLogo = designLogos.find((x) => x.logoId === logo.logoId);
          const viewLabel = designLogo
            ? (placementLabels.get(designLogo.inDesignId) ??
              designLogo.viewName?.toUpperCase?.() ??
              "")
            : "";
          const logoVariant = designLogo
            ? getLogoType(designLogo.logo)
            : "name";
          const instanceLogo: CartItemDesignInstanceLogo = {
            value: logo.value,
            logoId: logo.logoId,
            deliveryType: "Delivery",
            locationIdentifier: viewLabel,
            logoVariant: logoVariant,
            designLogoInDesignId: designLogo?.inDesignId,
          };
          return instanceLogo;
        }) as CartItemDesignInstanceLogo[],
      };
    }) as CartItemDesignInstance[];

    updateInCart({ ...cartItemDesign, instances } as CartItemDesign);
  }

  async function handleCommitOrder(
    orgId: number,
    orderId: number,
    orderDeliveryPreferences: OrderDeliveryPreferences,
    orderlines?: CartItem[],
  ): Promise<Order> {
    async function transaction() {
      const processed = applyLogoVariantLines(orderlines ?? items);
      return await orderRepository.commitOrder(
        orgId,
        orderId,
        orderDeliveryPreferences,
        processed,
      );
    }

    return withToasts(
      transaction,
      "Placing order. This may take a moment, please don't close the application.",
      "Order placed succesfully",
      "Failed to place order",
      setIsLoading,
    );
  }

  async function handlePlaceOrder(
    orgId: number,
    orderDeliveryPreferences: OrderDeliveryPreferences,
    orderlines?: CartItem[],
  ): Promise<string> {
    async function transaction() {
      const processed = applyLogoVariantLines(orderlines ?? items);
      return await orderRepository.placeOrder(
        orgId,
        orderDeliveryPreferences,
        processed,
      );
    }

    return withToasts(
      transaction,
      "Placing order...",
      "Order placed successfully",
      "Failed to place order",
      setIsLoading,
    );
  }

  function mapOrderLinesToOrderItems(orderlines: OrderLine[]): CartItem[] {
    // Unfortunately, TS doesn't recognize this as a non-nullable list, despite the filter at the end.
    // Visual Code appears to be able to understand this, but the language server cannot.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore -- don't remove this line, it's a workaround for a TypeScript bug
    return orderlines
      .filter((line) => !line.relatedTo)
      .map((orderline) => {
        switch (orderline.product.productType) {
          case "Logo": {
            const logo = (orderline as OrderLineLogo).baseLogo;
            const cartItem = mapBaseLogoToCartItemLogo(
              logo,
              (orderline as OrderLineLogo).toDelivery,
              (orderline as OrderLineLogo).toStorage,
            );
            return cartItem;
          }
          case "Design": {
            const design = (orderline as OrderLineDesign).design;
            const instances = mapInstances(orderline as OrderLineDesign);
            const cartItem = mapDesignToCartItemLogo(design, instances);
            return cartItem;
          }
        }
        return null;
      })
      .filter((line) => line != null);
  }

  function mapInstances(orderline: OrderLineDesign): CartItemDesignInstance[] {
    const placementLabels = getDesignLogoPlacementMap(orderline.design);
    const designLogos = orderline.design.designLogos ?? [];
    const instances: CartItemDesignInstance[] = orderline.instances.map((x) => {
      return {
        label: x.label,
        size: x.size,
        toDelivery: x.quantity,
        toStorage: 0,
        casing: orderline.casing,
        logos: x.logos.map((logo) => {
          const designLogo = designLogos.find((d) => d.logoId === logo.logoId);
          const placement = designLogo
            ? (placementLabels.get(designLogo.inDesignId) ??
              designLogo.viewName?.toUpperCase?.() ??
              "")
            : "";
          const cartItem: CartItemDesignInstanceLogo = {
            deliveryType: "Delivery",
            locationIdentifier: placement,
            logoId: logo.logoId,
            logoVariant: logo.isNumeric ? "number" : "name",
            value: logo.value,
            designLogoInDesignId: designLogo?.inDesignId,
          };
          return cartItem;
        }),
      };
    });

    return instances;
  }

  async function handleReorder(order: Order) {
    const lines: OrderLine[] = order.lines;
    const cartItems = mapOrderLinesToOrderItems(lines);
    return await addItems(cartItems).then(() => {
      navigate("/order/place");
    });
  }

  async function handleChangeOrder(order: Order) {
    const lines: OrderLine[] = order.lines;
    const cartItems = mapOrderLinesToOrderItems(lines);
    // TODO duplicate and set order lines
    await clearItems();
    const items = await addItems(cartItems);
    setActiveOrder({ ...order, lines: [] });
    return items;
  }

  const { getHeatingAndPackaging, getNavisionItemPrice } = useApiClient();

  async function handleChangePackagingOrDelivery(
    order: Order,
    deliveryType: DeliveryType,
    packaging: Packaging,
    deliveryBefore12PM?: boolean,
  ): Promise<Order> {
    setIsLoading(true);

    const isCartForThisOrder = activeOrder?.id === order.id;
    const cartItems =
      isCartForThisOrder && items && items.length > 0
        ? items
        : mapOrderLinesToOrderItems(order.lines);

    let packagingFees: {
      itemNo: string;
      price: number;
      name: string;
      quantity: number;
    }[] = [];

    if (
      packaging === Packaging.StandardBagPackaging ||
      packaging === Packaging.SpecificPackaging
    ) {
      try {
        const heatingAndPackaging = await getHeatingAndPackaging(
          order.organization.id,
        );
        const designs = items
          .filter((x) => x.type === "Design")
          .map((x) => x as CartItemDesign);

        // Group quantities by heating code
        const heatingGroups = new Map<string, number>();

        designs.forEach((d) => {
          const heatingCode = d.design.heating?.sku;
          if (heatingCode) {
            const qty = d.instances.reduce(
              (sum, i) => sum + (i.toDelivery ?? 0),
              0,
            );
            heatingGroups.set(
              heatingCode,
              (heatingGroups.get(heatingCode) ?? 0) + qty,
            );
          }
        });

        if (packaging === Packaging.SpecificPackaging) {
          // Specific packaging: Only one line, quantity 1. Use the first valid item number found.
          // Or find the specific item number associated with any of the heating codes?
          // The requirement implies there's a specific item number per heating code, but we only want ONE line.
          // "we still need to use the product number from the NAV endpoint" -> implies fetching it.
          // If multiple designs have different heating codes, which item number do we pick?
          // "Specific bag packaging is hours-based". Usually this means a single service item.
          // Let's assume we take the first valid mapping we find, or maybe there's a unique specific packaging item?
          // If the mapping returns different specific item numbers for different heating codes, we might have a conflict.
          // But the user says "only add 1 orderline... quantity... 1pcs".
          // Let's grab the first valid itemNo for specific packaging.

          let specificItemNo: string | undefined;
          for (const [heatingCode] of Array.from(heatingGroups.entries())) {
            const mapping = heatingAndPackaging.find(
              (x: any) => x.heatItemNo === heatingCode,
            );
            if (mapping?.specific) {
              specificItemNo = mapping.specific;
              break; // Found one, stop.
            }
          }

          if (specificItemNo) {
            const navItem = await getNavisionItemPrice(
              order.organization.id,
              specificItemNo,
              order.organization.navNumber,
            );
            // Specific packaging usually has a fixed price or base price, but quantity is 1 here.
            // We use the fetched price for 1 unit.
            const priceList = navItem.prices || [];
            const priceObj =
              getProductPrice(priceList, 1) ?? priceList[priceList.length - 1];
            const unitPrice =
              priceObj?.adjustedCustomerPrice ?? priceObj?.unitPrice ?? 0;

            packagingFees.push({
              itemNo: specificItemNo,
              price: unitPrice,
              name: "Specific Packaging", // Hardcoded name
              quantity: 1, // Always 1
            });
          }
        } else {
          // Standard Packaging: Loop through all groups
          for (const [heatingCode, quantity] of Array.from(
            heatingGroups.entries(),
          )) {
            const mapping = heatingAndPackaging.find(
              (x: any) => x.heatItemNo === heatingCode,
            );
            if (mapping) {
              const itemNo = mapping.packingItemNo;
              if (itemNo) {
                const navItem = await getNavisionItemPrice(
                  order.organization.id,
                  itemNo,
                  order.organization.navNumber,
                );
                const priceList = navItem.prices || [];
                const priceObj =
                  getProductPrice(priceList, quantity) ??
                  priceList[priceList.length - 1];
                const unitPrice =
                  priceObj?.adjustedCustomerPrice ?? priceObj?.unitPrice ?? 0;

                let packagingName = "Packaging";
                if (itemNo === "84")
                  packagingName = "Standard packaging (Bags)";
                else if (itemNo === "85")
                  packagingName = "Standard packaging (Jackets)";
                else if (itemNo === "86")
                  packagingName = "Standard packaging (Sweatshirt)";
                else if (itemNo === "87")
                  packagingName = "Standard packaging (Polo shirt)";
                else if (itemNo === "88")
                  packagingName = "Standard packaging (T-shirt)";
                else if (itemNo === "89")
                  packagingName = "Standard packaging (Workwear)";

                packagingFees.push({
                  itemNo: itemNo,
                  price: unitPrice,
                  name: packagingName,
                  quantity: quantity,
                });
              }
            }
          }
        }
      } catch (e) {
        console.error("Error fetching packaging prices", e);
      }
    }

    const draftOrder = buildLocalOrderDraft(
      order.organization.id,
      cartItems,
      currency ?? "DKK",
      organization,
      profile,
      {
        deliveryType,
        packaging,
        deliveryBefore12PM:
          deliveryBefore12PM ?? order.deliveryBefore12PMwithGLS ?? false,
        packagingFees, // Pass pre-calculated fees
      },
    );
    setActiveOrder(draftOrder);
    setIsLoading(false);
    return Promise.resolve(draftOrder);
  }

  return {
    isLoading,
    handleLogoAddOrderLine,
    handleLogoAddOrderLines,
    handleDesignAddOrderLine,
    handleDesignAddOrderLines,
    handleRemoveOrderLine,
    handleLogoSetQuantities,
    updateLogoOrderLine,
    handleDesignInstanceUpdateStorage,
    handlePlaceVerdict,
    handleCreateOrder,
    handleDownloadTemplate,
    handleUploadDesignTemplate,
    handleUploadLogoTemplate,
    handleCommitOrder,
    handlePlaceOrder,
    handleChangeOrder,
    handleChangePackagingOrDelivery,
    handleReorder,
  };
}

// Local lightweight draft builder to mirror OrderDto enough for the confirmation page without a backend roundtrip.
function buildLocalOrderDraft(
  orgId: number,
  orderItems: CartItem[],
  currency: string,
  organization?: Organization,
  profile?: Profile,
  options?: {
    deliveryType?: DeliveryType;
    packaging?: Packaging;
    deliveryBefore12PM?: boolean;
    packagingFees?: {
      itemNo: string;
      price: number;
      name: string;
      quantity: number;
    }[];
  },
): Order {
  let nextId = 1;
  const lines: OrderLine[] = [];

  const isCustomer = profile?.userOrganizationInformations?.role === 0; // Role.EndUser is 0

  const getApplicablePrice = (prices: Price[], quantity: number): number => {
    if (!prices || prices.length === 0) return 0;
    const priceObj = getProductPrice(prices, quantity);

    if (!priceObj) return 0;

    return isCustomer &&
      priceObj.adjustedCustomerPrice !== undefined &&
      priceObj.adjustedCustomerPrice !== null &&
      priceObj.adjustedCustomerPrice > 0
      ? priceObj.adjustedCustomerPrice
      : priceObj.adjustedPrice;
  };

  const createAdditionalFeeLines = (
    baseLogoLineId: number,
    baseLogo: BaseLogo,
    baseQuantity: number,
  ): OrderLine[] => {
    const feeLines: OrderLine[] = [];
    const fees = baseLogo.product?.additionalFees ?? [];

    fees.forEach((fee) => {
      let feeTotal = 0;
      let unitPrice = fee.unitPrice ?? 0;
      let numberOfColors = null;
      if (
        fee.name.toLowerCase().includes("startup") ||
        fee.name.toLowerCase().includes("start-up") ||
        fee.name.toLowerCase().includes("opstart") ||
        fee.name.toLowerCase().includes("film")
      ) {
        const perColor = getApplicablePrice(fee.prices ?? [], baseQuantity);
        const type = baseLogo.transferMethod?.type?.toLowerCase() ?? "";
        const isEmbroidery = type === "broderi" || type === "embroidery";

        numberOfColors = isEmbroidery ? 1 : baseLogo.colors?.length || 1;
        unitPrice = perColor;
        feeTotal = perColor * numberOfColors;
      } else {
        // For non-startup fees, we also need to respect customer pricing
        // Assuming fee.prices exists for these fees too
        if (fee.prices && fee.prices.length > 0) {
          const price = getApplicablePrice(fee.prices, baseQuantity);
          unitPrice = price;
        } else {
          // Fallback if prices array is missing (though it should be there)
          unitPrice = fee.unitPrice ?? 0;
        }
        feeTotal = unitPrice * (fee.quantity ?? 1);
      }

      if (unitPrice > 0) {
        feeLines.push({
          id: nextId++,
          displayNo: "",
          type: "Additional Fee",
          price: unitPrice,
          unitPrice: unitPrice,
          adjustedPrice: unitPrice,
          adjustedUnitPrice: unitPrice,
          quantity: numberOfColors ?? fee.quantity,
          title: fee.name,
          toStorage: 0,
          toDelivery: 0,
          relatedTo: baseLogoLineId,
          product: {
            productNo: fee.itemNo,
            productType: "AdditionalFee",
            productName: fee.name,
          },
          deliveryType: "Delivery",
          additionalFees: [],
        });
      }
    });
    return feeLines;
  };

  // Group name/number logos by SKU to avoid duplicate parents when variant lines were already expanded.
  const nameNumberGroups: Record<
    string,
    {
      logo: CartItemLogo;
      deliveries: QuantityRow[];
      storages: QuantityRow[];
      values: {
        label: string | number | undefined;
        toDelivery: number;
        toStorage: number;
      }[];
    }
  > = {};

  const normalItems: CartItem[] = [];
  const processedDeliveryArrays = new Set<QuantityRow[]>();
  const processedStorageArrays = new Set<QuantityRow[]>();

  // Global storage tracking across all items
  const globalUsedStorage = new Map<string, number>();

  const getGlobalAvailableStorage = (baseLogo: BaseLogo, digit?: string) => {
    let total = 0;
    let key = "";

    if (baseLogo.logoType === "NumberLogo" && digit) {
      total =
        baseLogo.numberLogosInStorage?.find((x) => x.value === digit)
          ?.inStorage ?? 0;
      key = `${baseLogo.id}::${digit}`;
    } else {
      total = baseLogo.inStorage ?? 0;
      key = `${baseLogo.id}`;
    }

    const used = globalUsedStorage.get(key) ?? 0;
    return Math.max(0, total - used);
  };

  const consumeGlobalStorage = (
    baseLogo: BaseLogo,
    amount: number,
    digit?: string,
  ) => {
    const key =
      baseLogo.logoType === "NumberLogo" && digit
        ? `${baseLogo.id}::${digit}`
        : `${baseLogo.id}`;
    const used = globalUsedStorage.get(key) ?? 0;
    globalUsedStorage.set(key, used + amount);
  };

  // Pre-calculate global total quantities for each logo to determine correct price tier
  const globalLogoTotalQuantities = new Map<number, number>();

  // Track global net production for fees (to avoid duplicate fees)
  const globalFeeData = new Map<
    number,
    { netQty: number; parentLineId?: number; baseLogo: BaseLogo }
  >();

  // Shared storage allocator for the price-tier pre-pass. Tracks remaining
  // inStorage as we walk through every cart item / design instance so that
  // when multiple instances or cart items share a logo, storage is consumed
  // exactly once across them. Kept separate from globalUsedStorage (which
  // runs during line emission) to avoid double-consumption.
  const tierRemainingStorage = new Map<string, number>();
  const storageKey = (baseLogo: BaseLogo, digit?: string) =>
    baseLogo.logoType === "NumberLogo" && digit
      ? `${baseLogo.id}::${digit}`
      : `${baseLogo.id}`;
  // Negative stored values (e.g., over-consumed historic storage) are
  // treated as 0 so the price-tier pre-pass can't inflate payable quantities
  // by subtracting a negative "taken" amount from toDelivery.
  const initialStorageFor = (baseLogo: BaseLogo, digit?: string): number => {
    const raw =
      baseLogo.logoType === "NumberLogo" && digit
        ? (baseLogo.numberLogosInStorage?.find((x) => x.value === digit)
            ?.inStorage ?? 0)
        : (baseLogo.inStorage ?? 0);
    return Math.max(0, raw);
  };
  const takeFromTierStorage = (
    baseLogo: BaseLogo,
    amount: number,
    digit?: string,
  ): number => {
    const key = storageKey(baseLogo, digit);
    if (!tierRemainingStorage.has(key)) {
      tierRemainingStorage.set(key, initialStorageFor(baseLogo, digit));
    }
    const remaining = tierRemainingStorage.get(key) ?? 0;
    const taken = Math.min(amount, remaining);
    tierRemainingStorage.set(key, remaining - taken);
    return taken;
  };

  // Compute how much of a logo placement is actually paid for (delivery beyond
  // existing storage, plus any new toStorage). Mirrors the place-order page's
  // payableQuantity formula. The returned amount is in tier units (digit
  // applications for NumberLogos, pieces otherwise) so it can be added
  // directly to globalLogoTotalQuantities without the digit multiplier.
  const consumePayable = (
    baseLogo: BaseLogo,
    toDelivery: number,
    toStorage: number,
    value: string | number | undefined,
  ): number => {
    if (baseLogo.logoType === "NumberLogo" && value !== undefined) {
      const digits = value.toString().split("");
      let payable = 0;
      for (const digit of digits) {
        const taken = takeFromTierStorage(baseLogo, toDelivery, digit);
        payable +=
          Math.max(0, toDelivery - taken) + Math.max(0, toStorage);
      }
      return payable;
    }
    const taken = takeFromTierStorage(baseLogo, toDelivery);
    return Math.max(0, toDelivery - taken) + Math.max(0, toStorage);
  };

  const addPayableToGlobalQty = (baseLogo: BaseLogo, payable: number) => {
    if (payable <= 0) return;
    const current = globalLogoTotalQuantities.get(baseLogo.id) ?? 0;
    globalLogoTotalQuantities.set(baseLogo.id, current + payable);
  };

  for (const item of orderItems) {
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;
      const toDelivery = logoItem.toDelivery ?? 0;
      const toStorage = logoItem.toStorage ?? 0;
      if (toDelivery + toStorage <= 0) continue;
      const payable = consumePayable(
        logoItem.baseLogo,
        toDelivery,
        toStorage,
        logoItem.value,
      );
      addPayableToGlobalQty(logoItem.baseLogo, payable);
    } else if (item.type === "Design") {
      const designItem = item as CartItemDesign;
      designItem.instances.forEach((inst) => {
        const instDelivery = inst.toDelivery ?? 0;
        const instStorage = inst.toStorage ?? 0;
        if (instDelivery + instStorage <= 0) return;
        inst.logos.forEach((l) => {
          const designLogo = designItem.design.designLogos?.find(
            (d) => d.logoId === l.logoId,
          );
          const baseLogo = designLogo?.logo ?? l.baseLogo;
          if (!baseLogo) return;
          const payable = consumePayable(
            baseLogo,
            instDelivery,
            instStorage,
            l.value,
          );
          addPayableToGlobalQty(baseLogo, payable);
        });
      });
    }
  }

  for (const item of orderItems) {
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;
      const isNumberLogo = logoItem.baseLogo.logoType === "NumberLogo";
      const isNameLogo = logoItem.baseLogo.logoType === "NameLogo";
      const sku = logoItem.baseLogo.product?.sku ?? logoItem.id;

      if (isNumberLogo || isNameLogo) {
        if (!nameNumberGroups[sku]) {
          nameNumberGroups[sku] = {
            logo: logoItem,
            deliveries: [],
            storages: [],
            values: [],
          };
        }
        const group = nameNumberGroups[sku];

        const deliveryRows = logoItem.deliveryQuantities ?? [];
        if (
          deliveryRows.length > 0 &&
          !processedDeliveryArrays.has(deliveryRows)
        ) {
          group.deliveries.push(...deliveryRows);
          processedDeliveryArrays.add(deliveryRows);
        }

        const storageRows = logoItem.storageQuantities ?? [];
        if (
          storageRows.length > 0 &&
          !processedStorageArrays.has(storageRows)
        ) {
          group.storages.push(...storageRows);
          processedStorageArrays.add(storageRows);
        }
        // If there are no rows but a value/toDelivery is present, treat as value row.
        if (
          (logoItem.deliveryQuantities?.length ?? 0) === 0 &&
          (logoItem.storageQuantities?.length ?? 0) === 0 &&
          (logoItem.toDelivery ?? 0) + (logoItem.toStorage ?? 0) > 0
        ) {
          group.values.push({
            label: logoItem.value,
            toDelivery: logoItem.toDelivery ?? 0,
            toStorage: logoItem.toStorage ?? 0,
          });
        }
      } else {
        normalItems.push(item);
      }
    } else {
      normalItems.push(item);
    }
  }

  // Process normal items first
  for (const item of normalItems) {
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;

      // Use top-level properties as source of truth for normal items to avoid stale row data
      const deliveryQty = logoItem.toDelivery ?? 0;
      const storageQty = logoItem.toStorage ?? 0;

      // Check if this is a "To Storage" ONLY order (no delivery, positive storage)
      const isOnlyStorage = deliveryQty === 0 && storageQty > 0;

      // Parent line with image/title, now carries the delivery price/quantity.
      // If "Only Storage", parent carries the storage quantity.
      const parentQty = isOnlyStorage ? storageQty : deliveryQty;
      const parentLineId = nextId++;

      // If only storage, we use the storage price (unit price * 1, positive)
      // Wait, standard logic for "To Storage" line is:
      // price = baseUnitPrice * multiplier (where multiplier is 1)
      // productNo = "To Storage"
      // deliveryType = "Storage"
      // toStorage = qty
      // toDelivery = 0

      // Calculate price based on the correct quantity (global total)
      const baseUnitPrice = getApplicablePrice(
        logoItem.baseLogo.product?.prices ?? [],
        globalLogoTotalQuantities.get(logoItem.baseLogo.id) ?? parentQty,
      );

      const logoTitle = formatLogoTitleWithSize(logoItem.title, logoItem.baseLogo);

      lines.push({
        id: parentLineId,
        displayNo: "",
        type: "Logo",
        price: baseUnitPrice,
        unitPrice: baseUnitPrice,
        adjustedPrice: baseUnitPrice,
        adjustedUnitPrice: baseUnitPrice,
        quantity: parentQty,
        title: logoTitle,
        displayImage: logoItem.displayImage?.lowResUrl ?? "",
        toStorage: isOnlyStorage ? parentQty : (logoItem.toStorage ?? 0),
        toDelivery: isOnlyStorage ? 0 : (logoItem.toDelivery ?? 0),
        relatedTo: 0,
        product: {
          productNo: isOnlyStorage
            ? "To Storage"
            : (logoItem.baseLogo.product?.sku ?? ""),
          productSku: logoItem.baseLogo.product?.sku,
          productType: "Logo",
          productName: logoTitle,
        },
        deliveryType: isOnlyStorage ? "Storage" : "Delivery",
        additionalFees: [],
        baseLogo: logoItem.baseLogo,
      } as OrderLineLogo);

      // We need to track the first emitted line ID to attach fees to it later
      // Since the main line is now the delivery line (or storage line), we use its ID.
      let deliveryLineId: number | undefined = parentLineId;

      const emitLine = (
        label: string | number | undefined,
        qty: number,
        type: "Delivery" | "ToStorage" | "FromStorage",
        priceMultiplier: number = 1,
      ) => {
        // Skip redundant delivery sub-line
        if (type === "Delivery" && !isOnlyStorage) return;
        // Skip redundant storage sub-line if it's "Only Storage"
        if (type === "ToStorage" && isOnlyStorage) return;

        const multiplier = priceMultiplier;
        if (qty <= 0) return;

        const isFromStorage = type === "FromStorage";
        const isToStorage = type === "ToStorage";

        const baseUnitPrice = getApplicablePrice(
          logoItem.baseLogo.product?.prices ?? [],
          globalLogoTotalQuantities.get(logoItem.baseLogo.id) ?? qty,
        );

        // From Storage has negative price
        const unitPrice = isFromStorage
          ? baseUnitPrice * multiplier * -1
          : baseUnitPrice * multiplier;

        const lineId = nextId++;

        // SKU logic: "From Storage", "To Storage", or original
        let productNo = logoItem.baseLogo.product?.sku ?? "";
        if (isFromStorage) productNo = "From Storage";
        else if (isToStorage) productNo = "To Storage";

        const logoTitle = formatLogoTitleWithSize(logoItem.title, logoItem.baseLogo);

        const baseTitle =
          label !== undefined && label !== ""
            ? `${logoTitle} (${label})`
            : logoTitle;
        // No suffix in title as requested
        const title = baseTitle;

        // If From Storage, we want it related to the Delivery line if it exists
        // Otherwise fallback to parentLineId (shouldn't happen in valid flow, but safe fallback)
        const relatedToId =
          isFromStorage && deliveryLineId ? deliveryLineId : parentLineId;

        lines.push({
          id: lineId,
          displayNo: "",
          type: "Logo",
          price: unitPrice,
          unitPrice: unitPrice,
          adjustedPrice: unitPrice,
          adjustedUnitPrice: unitPrice,
          quantity: qty,
          title: title,
          displayImage: undefined,
          toStorage: isToStorage ? qty : 0,
          toDelivery: !isToStorage ? qty : 0,
          relatedTo: relatedToId,
          product: {
            productNo: productNo,
            productSku: logoItem.baseLogo.product?.sku,
            productType: "Logo",
            productName: title,
          },
          deliveryType: isToStorage ? "Storage" : "Delivery",
          additionalFees: [],
          baseLogo: logoItem.baseLogo,
        } as OrderLineLogo);

        // Fees are now handled once after all lines are emitted
      };

      // 1. To Delivery (Full amount) - Skip if Only Storage
      if (!isOnlyStorage) {
        emitLine(undefined, deliveryQty, "Delivery");
      }

      // 2. To Storage (Full amount) - Skip if Only Storage (already parent)
      emitLine(undefined, storageQty, "ToStorage");

      // 3. From Storage (Offset) - Only relevant if not Only Storage (since Only Storage implies no delivery)
      let fromStorage = 0;
      if (!isOnlyStorage) {
        const availableStorage = getGlobalAvailableStorage(logoItem.baseLogo);
        fromStorage = Math.min(deliveryQty, availableStorage);
        consumeGlobalStorage(logoItem.baseLogo, fromStorage);

        emitLine(undefined, fromStorage, "FromStorage");
      }

      // 4. Fees (Once, based on net production)
      // Attach fees to the main delivery line if it exists, otherwise the first available line (e.g. storage)
      const feeParentId = deliveryLineId ?? lines[lines.length - 1]?.id;

      const netProductionQty = deliveryQty - fromStorage + storageQty;
      // Only charge fees if we are actually producing new logos
      // i.e., netProductionQty > 0
      // However, the user specifically requested:
      // "if a logo is ordering only 'From Storage', and the quantity to delivery is >= than the quantity in storage, and there's no 'To Storage', then we should not include any additional fees."
      // This implies: if (deliveryQty <= fromStorage && storageQty === 0) -> No fees.
      // Which is covered by netProductionQty > 0 check, because:
      // if deliveryQty <= fromStorage, then deliveryQty - fromStorage <= 0.
      // if storageQty == 0, then netProductionQty <= 0.
      // So the existing check `netProductionQty > 0` already covers this?
      // Wait, fromStorage = Math.min(deliveryQty, availableStorage).
      // So if availableStorage >= deliveryQty, then fromStorage = deliveryQty.
      // Then deliveryQty - fromStorage = 0.
      // If storageQty = 0, then netProductionQty = 0.
      // So yes, netProductionQty > 0 covers "Only charge fees if we are producing new logos".

      if (netProductionQty > 0) {
        const current = globalFeeData.get(logoItem.baseLogo.id) ?? {
          netQty: 0,
          baseLogo: logoItem.baseLogo,
        };
        current.netQty += netProductionQty;
        if (!current.parentLineId && feeParentId !== undefined) {
          current.parentLineId = feeParentId;
        }
        globalFeeData.set(logoItem.baseLogo.id, current);
      }
    } else if (item.type === "Design") {
      const designItem = item as CartItemDesign;
      const quantity = designItem.instances.reduce(
        (sum, inst) => sum + (inst?.toDelivery ?? 0) + (inst?.toStorage ?? 0),
        0,
      );
      // Main design line has 0 price, sub-items carry the price
      const unitPrice = 0;

      const lineId = nextId++;
      lines.push({
        id: lineId,
        displayNo: "",
        type: "Design",
        price: unitPrice,
        unitPrice: unitPrice,
        adjustedPrice: unitPrice,
        adjustedUnitPrice: unitPrice,
        quantity: quantity,
        title: designItem.title,
        displayImage: designItem.displayImage?.lowResUrl ?? "",
        toStorage: 0,
        toDelivery: quantity,
        relatedTo: 0,
        product: {
          productNo: designItem.design.product?.mul ?? "",
          productMul: designItem.design.product?.mul,
          productType: "Design",
          productName: designItem.title,
        },
        deliveryType: "Delivery",
        additionalFees: [],
        design: designItem.design,
        instances: designItem.instances.map((inst) => ({
          label: inst.label,
          quantity: inst.toDelivery,
          size: inst.size,
          logos: inst.logos.map((l) => ({
            value: l.value,
            isNumeric: l.logoVariant === "number",
            logoId: l.logoId,
          })),
        })),
      } as OrderLineDesign);

      // Heating line (related to design)
      if (designItem.design.heating) {
        const heatingQty = designItem.instances.reduce((total, inst) => {
          const instQty = (inst.toDelivery ?? 0) + (inst.toStorage ?? 0);
          if (instQty <= 0) return total;

          const instanceMultiplier = inst.logos.filter((l) => {
            const designLogo = designItem.design.designLogos?.find(
              (d) => d.logoId === l.logoId,
            );
            const baseLogo = designLogo?.logo ?? l.baseLogo;
            const type = baseLogo?.transferMethod?.type?.toLowerCase() ?? "";
            return (
              type !== "broderi" &&
              type !== "embroidery" &&
              type !== "direk tryk" &&
              type !== "direkte tryk"
            );
          }).length;

          return total + instQty * instanceMultiplier;
        }, 0);

        if (heatingQty > 0) {
          const heatingUnitPrice = getApplicablePrice(
            designItem.design.heating.prices ?? [],
            heatingQty,
          );

          lines.push({
            id: nextId++,
            displayNo: "",
            type: "Heating",
            price: heatingUnitPrice,
            unitPrice: heatingUnitPrice,
            adjustedPrice: heatingUnitPrice,
            adjustedUnitPrice: heatingUnitPrice,
            quantity: heatingQty,
            title: designItem.design.heating.name,
            displayImage: designItem.displayImage?.lowResUrl ?? "",
            toStorage: 0,
            toDelivery: heatingQty,
            relatedTo: lineId,
            product: {
              productNo: designItem.design.heating.sku,
              productSku: designItem.design.heating.sku,
              productType: "Heating",
              productName: designItem.design.heating.name,
            },
            deliveryType: "Delivery",
            additionalFees: [],
          } as OrderLine);
        }
      }

      // Create child logo lines: Aggregate quantities for static logos across all instances
      // Variable logos (Names/Numbers) are kept separate per value
      const logoAggregator = new Map<
        string,
        {
          quantity: number;
          logoSpec: CartItemDesignInstanceLogo;
          baseLogo?: BaseLogo;
        }
      >();

      designItem.instances.forEach((inst) => {
        inst.logos.forEach((logoSpec) => {
          const designLogo =
            designItem.design.designLogos?.find(
              (d) => d.logoId === logoSpec.logoId,
            ) ?? undefined;

          // Prefer designLogo.logo (fresh from backend) to ensure latest inStorage count
          const baseLogo = designLogo?.logo ?? logoSpec.baseLogo;
          const isNumberLogo = baseLogo?.logoType === "NumberLogo";
          const isNameLogo = baseLogo?.logoType === "NameLogo";

          // For static logos, key by logoId only (merges all instances).
          // For variable logos, key by logoId + value.
          let key = `${logoSpec.logoId}`;
          if (isNumberLogo || isNameLogo) {
            key += `::${logoSpec.value ?? ""}`;
          }

          if (!logoAggregator.has(key)) {
            logoAggregator.set(key, {
              quantity: 0,
              logoSpec,
              baseLogo,
            });
          }
          const entry = logoAggregator.get(key)!;
          // Accumulate delivery quantity
          entry.quantity += inst.toDelivery ?? 0;
        });
      });

      const processedFeeLogos = new Set<number>();
      const totalQuantitiesPerLogo = new Map<number, number>();
      const totalFromStoragePerLogo = new Map<number, number>();

      // Pre-calculate total effective quantities per logo (including digit multipliers)
      const feeData = new Map<number, { lineId: number; baseLogo: BaseLogo }>();

      logoAggregator.forEach(({ quantity, logoSpec, baseLogo }) => {
        if (!baseLogo) return;
        const isNumberLogo = baseLogo.logoType === "NumberLogo";
        let multiplier = 1;
        if (isNumberLogo) {
          const digits = `${logoSpec.value ?? ""}`;
          multiplier = digits.length || 1;
        }
        const current = totalQuantitiesPerLogo.get(baseLogo.id) ?? 0;
        totalQuantitiesPerLogo.set(
          baseLogo.id,
          current + quantity * multiplier,
        );
      });

      logoAggregator.forEach(({ quantity, logoSpec, baseLogo }) => {
        if (quantity <= 0) return;

        const emitInstanceLogoLine = (
          displayVal: string | number | undefined,
          qty: number,
          priceMultiplier: number = 1,
        ) => {
          const baseLogoTitle = baseLogo
            ? formatLogoTitleWithSize(baseLogo.title, baseLogo)
            : designItem.title;

          const logoQty = qty;
          const baseUnitPrice =
            baseLogo && baseLogo.product
              ? getApplicablePrice(
                  baseLogo.product.prices ?? [],
                  globalLogoTotalQuantities.get(baseLogo.id) ?? logoQty,
                )
              : 0;
          const logoUnitPrice = baseUnitPrice;

          const logoLineId = nextId++;
          lines.push({
            id: logoLineId,
            displayNo: "",
            type: "Logo",
            price: logoUnitPrice,
            adjustedPrice: logoUnitPrice,
            adjustedUnitPrice: logoUnitPrice,
            unitPrice: logoUnitPrice,
            quantity: logoQty * priceMultiplier,
            title:
              displayVal !== undefined && displayVal !== ""
                ? `${baseLogoTitle} (${displayVal})`
                : baseLogoTitle,
            displayImage: baseLogo?.displayImage?.lowResUrl ?? "",
            toStorage: 0,
            toDelivery: logoQty,
            relatedTo: lineId,
            product: {
              productNo: baseLogo?.product?.sku ?? "",
              productSku: baseLogo?.product?.sku,
              productType: "Logo",
              productName:
                displayVal !== undefined && displayVal !== ""
                  ? `${baseLogoTitle} (${displayVal})`
                  : baseLogoTitle,
            },
            deliveryType:
              logoSpec.deliveryType === "Application"
                ? "Application"
                : "Delivery",
            additionalFees: [],
            baseLogo: baseLogo as BaseLogo,
          } as OrderLineLogo);

          if (baseLogo) {
            // Check storage availability
            let fromStorageQty = 0;
            const isNumberLogo = baseLogo.logoType === "NumberLogo";

            if (isNumberLogo) {
              const digits = displayVal?.toString().split("") ?? [];
              digits.forEach((digit) => {
                const avail = getGlobalAvailableStorage(baseLogo, digit);
                const take = Math.min(qty, avail); // qty is number of garments
                consumeGlobalStorage(baseLogo, take, digit);
                fromStorageQty += take;
              });
            } else {
              const avail = getGlobalAvailableStorage(baseLogo);
              const take = Math.min(qty, avail);
              consumeGlobalStorage(baseLogo, take);
              fromStorageQty = take;
            }

            if (fromStorageQty > 0) {
              const fromStoragePrice = baseUnitPrice * -1;
              const fromStorageLineId = nextId++;
              lines.push({
                id: fromStorageLineId,
                displayNo: "",
                type: "Logo",
                price: fromStoragePrice,
                unitPrice: fromStoragePrice,
                adjustedPrice: fromStoragePrice,
                adjustedUnitPrice: fromStoragePrice,
                quantity: fromStorageQty,
                title: baseLogoTitle,
                displayImage: undefined,
                toStorage: 0,
                toDelivery: fromStorageQty,
                relatedTo: logoLineId,
                product: {
                  productNo: "From Storage",
                  productSku: baseLogo.product?.sku,
                  productType: "Logo",
                  productName: baseLogoTitle,
                },
                deliveryType:
                  logoSpec.deliveryType === "Application"
                    ? "Application"
                    : "Delivery",
                additionalFees: [],
                baseLogo: baseLogo,
              } as OrderLineLogo);
            }

            // Track total from storage for fee calculation
            const currentFromStorage =
              totalFromStoragePerLogo.get(baseLogo.id) ?? 0;
            totalFromStoragePerLogo.set(
              baseLogo.id,
              currentFromStorage + fromStorageQty,
            );

            // Store fee data for later processing
            if (!feeData.has(baseLogo.id)) {
              feeData.set(baseLogo.id, {
                lineId: logoLineId,
                baseLogo: baseLogo,
              });
            }
          }
        };

        const isNumberLogo = baseLogo?.logoType === "NumberLogo";
        const isNameLogo = baseLogo?.logoType === "NameLogo";

        if (isNumberLogo) {
          const digits = `${logoSpec.value ?? ""}`;
          const priceMultiplier = digits.length || 1;
          emitInstanceLogoLine(digits, quantity, priceMultiplier);
        } else if (isNameLogo) {
          emitInstanceLogoLine(logoSpec.value, quantity, 1);
        } else {
          emitInstanceLogoLine(undefined, quantity, 1);
        }
      });

      // Process fees after all lines are emitted
      feeData.forEach(({ lineId, baseLogo }, baseLogoId) => {
        const totalQty = totalQuantitiesPerLogo.get(baseLogoId) ?? 0;
        const totalFromStorage = totalFromStoragePerLogo.get(baseLogoId) ?? 0;
        const netQty = totalQty - totalFromStorage;

        if (netQty > 0) {
          const current = globalFeeData.get(baseLogo.id) ?? {
            netQty: 0,
            baseLogo: baseLogo,
          };
          current.netQty += netQty;
          if (!current.parentLineId) {
            current.parentLineId = lineId;
          }
          globalFeeData.set(baseLogo.id, current);
        }
      });
    }
  }

  // Process grouped name/number logos (one parent per SKU)
  for (const sku of Object.keys(nameNumberGroups)) {
    const group = nameNumberGroups[sku];
    const logoItem = group.logo;

    const rowsDeliveries = group.deliveries;
    const rowsStorages = group.storages;
    const isGroupNumberLogo = logoItem.baseLogo.logoType === "NumberLogo";

    // Aggregate rows by label to avoid duplicates
    const aggregateRows = (rows: QuantityRow[]) => {
      const map = new Map<string, number>();
      rows.forEach((r) => {
        const key = `${r.label ?? ""}`;
        map.set(key, (map.get(key) ?? 0) + r.quantity);
      });
      return Array.from(map.entries()).map(([label, quantity]) => ({
        label,
        quantity,
      }));
    };

    let aggDeliveries = aggregateRows(rowsDeliveries);
    let aggStorages = aggregateRows(rowsStorages);

    // Fallback to values if no rows exist
    if (
      aggDeliveries.length === 0 &&
      aggStorages.length === 0 &&
      group.values.length > 0
    ) {
      const deliveryValues = group.values
        .filter((v) => v.toDelivery > 0)
        .map((v) => ({
          label: `${v.label ?? ""}`,
          quantity: v.toDelivery,
        }));
      const storageValues = group.values
        .filter((v) => v.toStorage > 0)
        .map((v) => ({
          label: `${v.label ?? ""}`,
          quantity: v.toStorage,
        }));

      aggDeliveries = aggregateRows(deliveryValues);
      aggStorages = aggregateRows(storageValues);
    } else if (group.values.length > 0) {
      // Merge values into existing aggregations if they exist but were not captured in rows
      const deliveryValues = group.values
        .filter((v) => v.toDelivery > 0)
        .map((v) => ({
          label: `${v.label ?? ""}`,
          quantity: v.toDelivery,
        }));
      const storageValues = group.values
        .filter((v) => v.toStorage > 0)
        .map((v) => ({
          label: `${v.label ?? ""}`,
          quantity: v.toStorage,
        }));

      // Merge helper
      const merge = (
        existing: { label: string; quantity: number }[],
        newRows: { label: string; quantity: number }[],
      ) => {
        const map = new Map<string, number>();
        existing.forEach((r) => map.set(r.label, r.quantity));
        newRows.forEach((r) =>
          map.set(r.label, (map.get(r.label) ?? 0) + r.quantity),
        );
        return Array.from(map.entries()).map(([label, quantity]) => ({
          label,
          quantity,
        }));
      };

      aggDeliveries = merge(aggDeliveries, deliveryValues);
      aggStorages = merge(aggStorages, storageValues);
    }

    // Parent line
    const rowsQty =
      aggDeliveries.reduce((sum, r) => sum + r.quantity, 0) +
      aggStorages.reduce((sum, r) => sum + r.quantity, 0);
    const parentQty = rowsQty;
    const parentLineId = nextId++;

    const logoTitle = formatLogoTitleWithSize(logoItem.title, logoItem.baseLogo);

    lines.push({
      id: parentLineId,
      displayNo: "",
      type: "Logo",
      price: 0,
      unitPrice: 0,
      adjustedPrice: 0,
      adjustedUnitPrice: 0,
      quantity: parentQty,
      title: logoTitle,
      displayImage: logoItem.displayImage?.lowResUrl ?? "",
      toStorage: logoItem.toStorage ?? 0,
      toDelivery: logoItem.toDelivery ?? 0,
      relatedTo: 0,
      product: {
        productNo: logoItem.baseLogo.product?.sku ?? "",
        productSku: logoItem.baseLogo.product?.sku,
        productType: "Logo",
        productName: logoTitle,
      },
      deliveryType:
        (logoItem.toDelivery ?? 0) > 0
          ? "Delivery"
          : (logoItem.toStorage ?? 0) > 0
            ? "Storage"
            : "Delivery",
      additionalFees: [],
      baseLogo: logoItem.baseLogo,
    } as OrderLineLogo);

    // We need to track the first emitted line ID to attach fees to it later
    // For grouped items, we track delivery lines by digit/label to associate FromStorage lines correctly
    const deliveryLines = new Map<string, number>();

    const emitLine = (
      label: string | number | undefined,
      qty: number,
      type: "Delivery" | "ToStorage" | "FromStorage",
      priceMultiplier: number = 1,
    ) => {
      const multiplier = priceMultiplier;
      if (qty <= 0) return;

      const isFromStorage = type === "FromStorage";
      const isToStorage = type === "ToStorage";

      const baseUnitPrice = getApplicablePrice(
        logoItem.baseLogo.product?.prices ?? [],
        globalLogoTotalQuantities.get(logoItem.baseLogo.id) ?? qty,
      );

      const unitPrice = isFromStorage
        ? baseUnitPrice * multiplier * -1
        : baseUnitPrice * multiplier;

      const lineId = nextId++;

      let productNo = logoItem.baseLogo.product?.sku ?? "";
      if (isFromStorage) productNo = "From Storage";
      else if (isToStorage) productNo = "To Storage";

      const logoTitle = formatLogoTitleWithSize(logoItem.title, logoItem.baseLogo);

      const baseTitle =
        label !== undefined && label !== ""
          ? `${logoTitle} (${label})`
          : logoTitle;
      const title = baseTitle;

      const lookupKey = label?.toString() ?? "";

      // Capture Delivery Line ID
      if (type === "Delivery") {
        deliveryLines.set(lookupKey, lineId);
      }

      // Associate FromStorage with corresponding Delivery line
      const deliveryLineId = deliveryLines.get(lookupKey);
      const relatedToId =
        isFromStorage && deliveryLineId ? deliveryLineId : parentLineId;

      lines.push({
        id: lineId,
        displayNo: "",
        type: "Logo",
        price: unitPrice,
        unitPrice: unitPrice,
        adjustedPrice: unitPrice,
        adjustedUnitPrice: unitPrice,
        quantity: qty,
        title: title,
        displayImage: undefined,
        toStorage: isToStorage ? qty : 0,
        toDelivery: !isToStorage ? qty : 0,
        relatedTo: relatedToId,
        product: {
          productNo: productNo,
          productSku: logoItem.baseLogo.product?.sku,
          productType: "Logo",
          productName: title,
        },
        deliveryType: isToStorage ? "Storage" : "Delivery",
        additionalFees: [],
        baseLogo: logoItem.baseLogo,
      } as OrderLineLogo);

      // Fees handled once per group at the end
    };

    // Tracking used storage for digits across multiple aggregated rows
    // const usedStorage = new Map<string, number>();

    const getAvailableStorage = (digit: string) => {
      if (isGroupNumberLogo) {
        return getGlobalAvailableStorage(logoItem.baseLogo, digit);
      } else {
        // For NameLogo or others, use main inStorage
        return getGlobalAvailableStorage(logoItem.baseLogo);
      }
    };

    const consumeStorage = (digit: string, amount: number) => {
      if (isGroupNumberLogo) {
        consumeGlobalStorage(logoItem.baseLogo, amount, digit);
      } else {
        consumeGlobalStorage(logoItem.baseLogo, amount);
      }
    };

    // Child lines: either from rows or fallback values
    aggDeliveries.forEach((row) => {
      const label = row.label; // e.g. "10"

      if (isGroupNumberLogo && label) {
        // Split number into digits
        const digits = label.toString().split("");
        digits.forEach((digit) => {
          // Each digit is a separate item of quantity `row.quantity`
          const qty = row.quantity;
          const avail = getAvailableStorage(digit);
          const fromStorage = Math.min(qty, avail);

          consumeStorage(digit, fromStorage);

          emitLine(digit, qty, "Delivery", 1);
          // Stamp the original full number on the just-emitted Delivery line so
          // the typed-numbers popover can collapse per-digit lines into one row
          // per typed number ("12", not "1" + "2"). FromStorage lines are not
          // groupable (filtered by isRealSku) so they don't need stamping.
          const lastDelivery = lines[lines.length - 1];
          if (lastDelivery && qty > 0) {
            lastDelivery.typedNumberValue = label.toString();
          }
          emitLine(digit, fromStorage, "FromStorage", 1);
        });
      } else {
        // NameLogo or non-split logic
        const qty = row.quantity;
        const avail = getAvailableStorage("");
        const fromStorage = Math.min(qty, avail);

        consumeStorage("", fromStorage);

        emitLine(label, qty, "Delivery", 1);
        emitLine(label, fromStorage, "FromStorage", 1);
      }
    });

    aggStorages.forEach((row) => {
      const label = row.label;
      if (isGroupNumberLogo && label) {
        const digits = label.toString().split("");
        digits.forEach((digit) => {
          emitLine(digit, row.quantity, "ToStorage", 1);
        });
      } else {
        emitLine(label, row.quantity, "ToStorage", 1);
      }
    });

    // One-time fee calculation for the entire group
    let totalNetProduction = 0;

    // Re-simulate logic to get net production
    const tempUsedStorage = new Map<string, number>();
    const getTempAvailable = (digit: string) => {
      const total = isGroupNumberLogo
        ? (logoItem.baseLogo.numberLogosInStorage?.find(
            (x) => x.value === digit,
          )?.inStorage ?? 0)
        : (logoItem.baseLogo.inStorage ?? 0);
      const used = tempUsedStorage.get(digit) ?? 0;
      return Math.max(0, total - used);
    };
    const consumeTemp = (digit: string, amt: number) => {
      tempUsedStorage.set(digit, (tempUsedStorage.get(digit) ?? 0) + amt);
    };

    aggDeliveries.forEach((row) => {
      if (isGroupNumberLogo && row.label) {
        row.label
          .toString()
          .split("")
          .forEach((digit) => {
            const avail = getTempAvailable(digit);
            const fromStorage = Math.min(row.quantity, avail);
            consumeTemp(digit, fromStorage);
            totalNetProduction += row.quantity - fromStorage;
          });
      } else {
        // NameLogo or others
        const key = isGroupNumberLogo ? "" : "main";
        const avail = getTempAvailable(key);
        const fromStorage = Math.min(row.quantity, avail);
        consumeTemp(key, fromStorage);
        totalNetProduction += row.quantity - fromStorage;
      }
    });

    aggStorages.forEach((row) => {
      if (isGroupNumberLogo && row.label) {
        totalNetProduction += row.quantity * row.label.toString().length;
      } else {
        totalNetProduction += row.quantity;
      }
    });

    if (totalNetProduction > 0 && lines.length > 0) {
      // Find a suitable parent line. First child line is okay.
      const firstChild = lines.find((l) => l.relatedTo === parentLineId);
      if (firstChild) {
        const current = globalFeeData.get(logoItem.baseLogo.id) ?? {
          netQty: 0,
          baseLogo: logoItem.baseLogo,
        };
        current.netQty += totalNetProduction;
        if (!current.parentLineId) {
          current.parentLineId = firstChild.id;
        }
        globalFeeData.set(logoItem.baseLogo.id, current);
      }
    }
  }

  // Generate all fee lines based on global net production
  globalFeeData.forEach((data) => {
    if (data.netQty > 0 && data.parentLineId) {
      const fees = createAdditionalFeeLines(
        data.parentLineId,
        data.baseLogo,
        data.netQty,
      );
      lines.push(...fees);
    }
  });

  // --- Delivery & Packaging Logic ---
  const deliveryType = options?.deliveryType ?? DeliveryType.Standard;
  const packagingType = options?.packaging ?? Packaging.NoPackaging;
  const deliveryBefore12PM = options?.deliveryBefore12PM ?? false;

  const totalItemsQuantity = lines
    .filter(
      (l) =>
        l.product.productType === "Design" && l.deliveryType === "Delivery",
    )
    .reduce((sum, l) => sum + l.quantity, 0);

  // 1. Packaging
  if (
    options?.packagingFees &&
    options.packagingFees.length > 0 &&
    (packagingType === Packaging.StandardBagPackaging ||
      packagingType === Packaging.SpecificPackaging)
  ) {
    options.packagingFees.forEach((fee) => {
      const isSpecific = packagingType === Packaging.SpecificPackaging;
      // For specific packaging, we might want to update the title to indicate adjustability
      const title = isSpecific
        ? `${fee.name} (QTY will be adjusted)`
        : fee.name;
      const totalCost = fee.price * fee.quantity;

      lines.push({
        id: nextId++,
        displayNo: "",
        type: "Packaging",
        price: fee.price,
        unitPrice: fee.price,
        adjustedPrice: fee.price,
        adjustedUnitPrice: fee.price,
        quantity: fee.quantity,
        title: title,
        toStorage: 0,
        toDelivery: fee.quantity,
        relatedTo: 0,
        product: {
          productNo: fee.itemNo,
          productType: "Packaging",
          productName: title,
        },
        deliveryType: "Delivery",
        additionalFees: [],
      } as OrderLine);
    });
  }

  // 2. Delivery Before 12PM
  // if (deliveryBefore12PM) {
  //   const cost = profile?.orderOptions?.orderOptionsDeliveryCost ?? 0;
  //   if (cost > 0) {
  //     lines.push({
  //       id: nextId++,
  //       displayNo: "",
  //       type: "AdditionalFee",
  //       price: cost,
  //       adjustedPrice: cost,
  //       adjustedUnitPrice: cost,
  //       quantity: 1,
  //       title: "Delivery before 12:00 PM with GLS",
  //       toStorage: 0,
  //       toDelivery: 1,
  //       relatedTo: 0,
  //       product: {
  //         productNo: "GLS12",
  //         productType: "Delivery",
  //         productName: "Delivery before 12:00 PM with GLS",
  //       },
  //       deliveryType: "Delivery",
  //       additionalFees: [],
  //     } as OrderLine);
  //   }
  // }

  // 3. Delivery Surcharge
  // Calculate subtotal for surcharge (excluding VAT, including other fees so far?)
  // Usually delivery fee is based on total goods value.
  const currentSubTotal = lines.reduce(
    (sum, l) => sum + (l.adjustedPrice || 0) * (l.quantity || 0),
    0,
  );

  let surchargePercent = 0;
  let expressTime = 24;
  if (deliveryType === DeliveryType.Express24) {
    surchargePercent = 1.0;
    expressTime = 24;
  } else if (deliveryType === DeliveryType.Express48) {
    surchargePercent = 0.5;
    expressTime = 48;
  } else if (deliveryType === DeliveryType.Express72) {
    surchargePercent = 0.25;
    expressTime = 72;
  }

  if (surchargePercent > 0) {
    const surcharge = currentSubTotal * surchargePercent;
    lines.push({
      id: nextId++,
      displayNo: "",
      type: "AdditionalFee",
      price: surcharge,
      adjustedPrice: surcharge,
      adjustedUnitPrice: surcharge,
      unitPrice: surcharge,
      quantity: 1,
      title: `Express Delivery ${expressTime} (${surchargePercent * 100}%)`,
      toStorage: 0,
      toDelivery: 1,
      relatedTo: 0,
      product: {
        productNo: `EXPRESS ${expressTime}`,
        productType: "Delivery",
        productName: `Express Delivery ${expressTime} (${
          surchargePercent * 100
        }%)`,
      },
      deliveryType: "Delivery",
      additionalFees: [],
    } as OrderLine);
  }

  const grouped = groupAndSortLocally(lines);
  assignDisplayNumbers(grouped);

  const subTotal = grouped.reduce(
    (sum, l) => sum + (l.adjustedPrice || 0) * (l.quantity || 0),
    0,
  );
  const vatRate = organization?.vatRate ?? 0.25;
  const vat = subTotal * vatRate;

  const now = new Date();

  return {
    id: 0,
    subTotal: subTotal,
    total: subTotal + vat,
    vat: vat,
    latestDelivery: now,
    createdAtUtc: now,
    updatedAtUtc: now,
    placedAtUtc: now,
    orderNumber: "",
    price: subTotal,
    currency: currency,
    status: OrderStatus.Draft,
    lines: grouped,
    heating: [],
    logoPrices: [],
    additionalFees: [],
    deliveryType: deliveryType,
    packagingType: packagingType,
    orderComment: "",
    originOfDelivery: "",
    requisitionNumber: "",
    organization: organization ?? {
      id: orgId,
      name: "",
      isVendor: false,
      excludedManufacturers: [],
      children: [],
      navNumber: "",
      country: "",
      disabled: false,
      hidePrices: false,
      type: "",
      contactPerson: "",
      contactEmail: "",
      contactPhone: "",
      address: "",
      cvr: "",
      totalOrderValue: 0,
      lastOrder: undefined,
      postCode: "",
      city: "",
      eori: "",
      shippingName: "",
      shippingAddress: "",
      shippingPostCode: "",
      shippingCity: "",
      shippingCountry: "",
      shippingContactPerson: "",
      shippingContactPhone: "",
      shippingContactEmail: "",
      parentId: undefined,
      mainBranchId: undefined,
      vatRate: 0,
      defaultVatRate: 0,
      parent: undefined,
      companyLogo: undefined,
      email: "",
      phone: "",
      commission: undefined,
    },
    approval: null,
    orderDeliveryAddress: undefined,
    deliveryBefore12PMwithGLS: deliveryBefore12PM,
  };
}

function groupAndSortLocally(lines: OrderLine[]): OrderLine[] {
  const result: OrderLine[] = [];
  const tops = lines.filter((l) => !l.relatedTo);
  const designs = tops.filter((l) => l.product.productType === "Design");
  const logos = tops.filter((l) => l.product.productType === "Logo");
  const others = tops.filter(
    (l) => !["Design", "Logo"].includes(l.product.productType),
  );

  const appendWithChildren = (line: OrderLine) => {
    result.push(line);
    const kids = lines
      .filter((l) => l.relatedTo === line.id)
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    kids.forEach((k) => appendWithChildren(k));
  };

  [...designs, ...logos, ...others].forEach((top) => appendWithChildren(top));

  return result;
}

function assignDisplayNumbers(lines: OrderLine[]): void {
  const topLevels = lines.filter((l) => !l.relatedTo);
  topLevels.forEach((line, idx) => {
    line.displayNo = (idx + 1).toString().padStart(2, "0");
  });

  lines
    .filter((l) => l.relatedTo)
    .forEach((line) => {
      const parent = lines.find((l) => l.id === line.relatedTo);
      const siblings = lines.filter((l) => l.relatedTo === line.relatedTo);
      const siblingIndex = siblings.indexOf(line) + 1;
      line.displayNo = `${parent?.displayNo ?? ""} - ${siblingIndex}`;
    });
}
